import type { LocalAudioTrack } from '@zoom/videosdk';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
// eslint-disable-next-line no-duplicate-imports
import { VideoCameraAddOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { Box, Button as ChakraButton, Flex, Grid, GridItem, Heading, Separator } from '@chakra-ui/react';
import ZoomVideo from '@zoom/videosdk';
import classNames from 'classnames';
import { useHistory, useLocation, useParams, type RouteComponentProps } from 'react-router-dom';
import { LESSONLOGTYPE } from '../../api/server';
import { UrlPramsPreview } from '../../App';
import { IconFont } from '../../component/icon-font';
import AppLogo from '../../component/image/AppLogo';
import VideoOffIcon from '../../component/image/VideoOffIcon';
import VideoOnIcon from '../../component/image/VideoOnIcon';
import LoadingLayer from '../../component/loading-layer';
import { IS_DEMO } from '../../config/env-config';
import { VBType } from '../../context/device-setting-context';
import ZoomContext from '../../context/zoom-context';
import { isValidLesson } from '../../helpers/lesson-helper';
import { useMount, useUnmount } from '../../hooks';
import useDeviceSettingContext from '../../hooks/useDeviceSettingContext';
import useErrorStateContext from '../../hooks/useErrorStateContext';
import useLanguageSelector from '../../hooks/useLanguageSelector';
import useLesson from '../../hooks/useLesson';
import useLogger from '../../hooks/useLogger';
import { useUser } from '../../hooks/useUser';
import { LocalVideoTrackEx } from '../../index-types';
import { JoinService } from '../../service/join-service';
import { LessonService } from '../../service/lesson-service';
import { isMobile } from '../../utils/DeviceType';
import { setLocalStorage } from '../../utils/web-storage-utils';
import type { MediaDevice } from '../video/video-types';
import './preview.scss';
import Settings from './Setting';
import SettingsButton from './SettingsButton';
import { SendLoggerService } from '../../service/send-logger-service';
type SelectItem = {
  label: string;
  value: string;
};
type MediaSetting = 'video' | 'audio';
// label: string;
// deviceId: string;
let prevMicFeedbackStyle = '';
let micFeedBackInteval: any = '';

let localAudio = ZoomVideo.createLocalAudioTrack() as LocalAudioTrack;
let localVideo = ZoomVideo.createLocalVideoTrack() as LocalVideoTrackEx;
let allDevices;

const mountDevices: () => Promise<{
  mics: MediaDevice[];
  speakers: MediaDevice[];
  cameras: MediaDevice[];
}> = async () => {
  allDevices = await ZoomVideo.getDevices();

  const cameraDevices: Array<MediaDeviceInfo> = allDevices.filter((device) => {
    return device.kind === 'videoinput';
  });
  const micDevices: Array<MediaDeviceInfo> = allDevices.filter((device) => {
    return device.kind === 'audioinput';
  });
  const speakerDevices: Array<MediaDeviceInfo> = allDevices.filter((device) => {
    return device.kind === 'audiooutput';
  });

  return {
    mics: micDevices.map((item) => {
      return { label: item.label, deviceId: item.deviceId };
    }),
    speakers: speakerDevices.map((item) => {
      return { label: item.label, deviceId: item.deviceId };
    }),
    cameras: cameraDevices.map((item) => {
      return { label: item.label, deviceId: item.deviceId };
    })
  };
};

const updateMicFeedbackStyle = () => {
  const newVolumeIntensity = localAudio.getCurrentVolume();
  let newMicFeedbackStyle = '';

  if (newVolumeIntensity === 0) {
    newMicFeedbackStyle = '';
  } else if (newVolumeIntensity <= 0.05) {
    newMicFeedbackStyle = 'mic-feedback__very-low';
  } else if (newVolumeIntensity <= 0.1) {
    newMicFeedbackStyle = 'mic-feedback__low';
  } else if (newVolumeIntensity <= 0.15) {
    newMicFeedbackStyle = 'mic-feedback__medium';
  } else if (newVolumeIntensity <= 0.2) {
    newMicFeedbackStyle = 'mic-feedback__high';
  } else if (newVolumeIntensity <= 0.25) {
    newMicFeedbackStyle = 'mic-feedback__very-high';
  } else {
    newMicFeedbackStyle = 'mic-feedback__max';
  }
  const micIcon: any = document.getElementById('auido-volume-feedback');
  if (prevMicFeedbackStyle !== '' && micIcon) {
    micIcon.classList.toggle(prevMicFeedbackStyle);
  }

  if (newMicFeedbackStyle !== '' && micIcon) {
    micIcon.classList.toggle(newMicFeedbackStyle);
  }
  console.log(newMicFeedbackStyle, newVolumeIntensity);
  prevMicFeedbackStyle = newMicFeedbackStyle;
};

const PreviewContainer: React.FunctionComponent<RouteComponentProps> = (props) => {
  const zmClient = useContext(ZoomContext);
  const LANG = useLanguageSelector();
  const search = useLocation().search;
  const currentPage = useLocation().pathname;
  const query = new URLSearchParams(search);
  const systemLanguage = query.get('lang');

  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [micList, setMicList] = useState<MediaDevice[]>([]);
  const [isStartedAudio, setIsStartedAudio] = useState<boolean>(micList.length > 0);
  const [speakerList, setSpeakerList] = useState<MediaDevice[]>([]);
  const [cameraList, setCameraList] = useState<MediaDevice[]>([]);
  const [isStartedVideo, setIsStartedVideo] = useState<boolean>(false);
  const [activeMicrophone, setActiveMicrophone] = useState('');
  const [activeSpeaker, setActiveSpeaker] = useState('');
  const [activeCamera, setActiveCamera] = useState('');
  const [activeVirtualBackground, setActiceVirtualBackground] = useState<VBType>('none');
  const [isInVBMode, setIsInVBMode] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const roomId = useParams<UrlPramsPreview>().roomId;
  const companyId = useParams<UrlPramsPreview>().companyId;
  const { userInfo } = useUser();
  const { lessonInfo } = useLesson(companyId, roomId);
  const [isMediaSetting, setIsMediaSetting] = useState(false);
  const deviceSettingCtx = useDeviceSettingContext();
  const [isMicrophoneForbidden, setIsMicrophoneForbidden] = useState(false);
  const joinService = useMemo(() => new JoinService(), []);
  const isInValidLesson = !!lessonInfo && !isValidLesson(lessonInfo);
  const errorCtx = useErrorStateContext();
  const logger = useLogger();
  const [isJoining, setIsJoining] = useState<boolean>(false);
  const lessonService = useMemo(() => new LessonService(), []);
  const [completedDeviceMount, setCompletedDeviceMount] = useState(false);
  const sendLoggerService = useMemo(() => new SendLoggerService(), []);

  const startLocalVideo = async () => {
    if (videoRef.current) {
      try {
        if (localVideo.isVideoStarted) {
          await localVideo.stop();
        }
        await localVideo?.start(videoRef.current);
        deviceSettingCtx.updateDeviceSetting((prev) => {
          return { ...prev, isStartedVideo: true };
        });
        setIsStartedVideo(true);
        logger.log('video start');
      } catch (error) {
        logger.error(error);
      }
    }
  };
  const startLocalCanvasVideo = async (imageUrl: VBType = 'none') => {
    if (canvasRef.current) {
      try {
        if (localVideo.isVideoStarted) {
          await localVideo.stop();
        }
        if (imageUrl === 'none') {
          await localVideo.start(canvasRef.current);
        } else {
          await localVideo.start(canvasRef.current, { imageUrl: imageUrl });
        }
        setIsStartedVideo(true);
        deviceSettingCtx.updateDeviceSetting((prev) => {
          return { ...prev, isStartedVideo: true };
        });
        logger.log('canvas video start');
      } catch (error) {
        logger.error(error);
      }
    }
    setIsInVBMode(true);
  };
  const onCameraClick = useCallback(
    async (isAuto: boolean = false) => {
      if (isStartedVideo) {
        sendLoggerService.sendLog({
          action: 'カメラボタン押下(ON->OFF)',
          lessonId: lessonInfo?.lessonId ?? '',
          roomId: lessonInfo?.roomId ?? '',
          userId: userInfo?.userId ?? '',
          page: currentPage,
          advancedInfo: {
            isAuto
          }
        });

        await localVideo.stop();
        setIsStartedVideo(false);
        deviceSettingCtx.updateDeviceSetting((prev) => {
          return { ...prev, isStartedVideo: false };
        });
        setIsInVBMode(false);
      } else {
        sendLoggerService.sendLog({
          action: 'カメラボタン押下(OFF->ON)',
          lessonId: lessonInfo?.lessonId ?? '',
          roomId: lessonInfo?.roomId ?? '',
          userId: userInfo?.userId ?? '',
          page: currentPage,
          advancedInfo: {
            isAuto
          }
        });

        if (deviceSettingCtx.virtualBackground === 'blur') {
          await startLocalCanvasVideo('blur');
        } else {
          await startLocalVideo();
        }
      }
    },
    [isStartedVideo]
  );

  const onMicrophoneClick = useCallback(
    async (isAuto: boolean = false) => {
      if (isStartedAudio) {
        if (isMuted) {
          sendLoggerService.sendLog({
            action: 'マイクボタン押下(Mute->Unmute)',
            lessonId: lessonInfo?.lessonId ?? '',
            roomId: lessonInfo?.roomId ?? '',
            userId: userInfo?.userId ?? '',
            page: currentPage,
            advancedInfo: {
              isAuto
            }
          });

          await localAudio?.unmute();
          micFeedBackInteval = setInterval(updateMicFeedbackStyle, 500);
          setIsMuted(false);
        } else {
          sendLoggerService.sendLog({
            action: 'マイクボタン押下(Unmute->Mute)',
            lessonId: lessonInfo?.lessonId ?? '',
            roomId: lessonInfo?.roomId ?? '',
            userId: userInfo?.userId ?? '',
            page: currentPage,
            advancedInfo: {
              isAuto
            }
          });

          await localAudio?.mute();
          if (micFeedBackInteval) {
            clearInterval(micFeedBackInteval);
          }
          setIsMuted(true);
        }
      } else {
        sendLoggerService.sendLog({
          action: 'マイクボタン押下(AudioON)',
          lessonId: lessonInfo?.lessonId ?? '',
          roomId: lessonInfo?.roomId ?? '',
          userId: userInfo?.userId ?? '',
          page: currentPage,
          advancedInfo: {
            isAuto
          }
        });

        try {
          await localAudio?.start();
          setIsStartedAudio(true);
          deviceSettingCtx.updateDeviceSetting((prev) => {
            return { ...prev, isStartedAudio: true };
          });
        } catch (e: any) {
          if (e.type === 'INSUFFICIENT_PRIVILEGES' && e.reason === 'USER_FORBIDDEN_MICROPHONE') {
            setIsMicrophoneForbidden(true);
          }
          console.warn(e);
        }
      }
    },
    [isStartedAudio, isMuted]
  );
  const onMicrophoneMenuClick = async (key: string) => {
    const [type, deviceId] = key.split('|');
    if (type === 'microphone') {
      if (deviceId !== activeMicrophone) {
        await localAudio.stop();
        setIsMuted(true);
        localAudio = ZoomVideo.createLocalAudioTrack(deviceId);
        await localAudio.start();
        setActiveMicrophone(deviceId);
      }
    } else if (type === 'leave audio') {
      await localAudio.stop();
      setIsStartedAudio(false);
      deviceSettingCtx.updateDeviceSetting((prev) => {
        return { ...prev, isStartedAudio: false };
      });
    }
  };
  const onSwitchCamera = async (key: string) => {
    if (localVideo) {
      if (activeCamera !== key) {
        await localVideo.switchCamera(key);
        setActiveCamera(key);
        console.debug(localVideo);
        deviceSettingCtx.updateDeviceSetting((prev) => {
          return { ...prev, activeCamera: key };
        });
      }
    }
  };
  const onSwitchMicrophone = (key: string) => {
    setActiveMicrophone(key);
    deviceSettingCtx.updateDeviceSetting((prev) => {
      return { ...prev, activeMicrophone: key };
    });
  };
  const onSwitchSpeaker = (key: string) => {
    setActiveSpeaker(key);
    deviceSettingCtx.updateDeviceSetting((prev) => {
      return { ...prev, activeSpeaker: key };
    });
  };
  // const onBlurBackground = useCallback(async () => {
  //   if (isInVBMode) {
  //     if (isBlur) {
  //       await localVideo.updateVirtualBackground(undefined);
  //     } else {
  //       await localVideo.updateVirtualBackground('blur');
  //     }
  //     setIsBlur(!isBlur);
  //   } else {
  //     if (!isBlur) {
  //       if (isStartedVideo && canvasRef.current) {
  //         await localVideo.stop();
  //         localVideo.start(canvasRef.current, { imageUrl: 'blur' });
  //       }
  //       setIsInVBMode(true);
  //       setIsBlur(true);
  //     }
  //   }
  // }, [isInVBMode, isBlur, isStartedVideo]);
  useMount(() => {
    mountDevices().then((devices) => {
      sendLoggerService.sendLog({
        action: 'デバイス準備完了',
        lessonId: lessonInfo?.lessonId ?? '',
        roomId: lessonInfo?.roomId ?? '',
        userId: userInfo?.userId ?? '',
        page: currentPage,
        advancedInfo: {
          devices: devices
        }
      });

      setMicList(devices.mics);
      setCameraList(devices.cameras);
      setSpeakerList(devices.speakers);
      if (devices.speakers.length > 0) {
        setActiveSpeaker(devices.speakers[0].deviceId);
        deviceSettingCtx.updateDeviceSetting((prev) => {
          return { ...prev, activeSpeaker: devices.speakers[0].deviceId };
        });
      }
      if (devices.mics.length > 0) {
        setActiveMicrophone(devices.mics[0].deviceId);
        deviceSettingCtx.updateDeviceSetting((prev) => {
          return { ...prev, activeMicrophone: devices.mics[0].deviceId };
        });
      }
      if (devices.cameras.length > 0) {
        deviceSettingCtx.updateDeviceSetting((prev) => {
          return { ...prev, activeCamera: devices.cameras[0].deviceId };
        });
      }

      Promise.allSettled([onMicrophoneClick(), onCameraClick()]).finally(() => {
        setCompletedDeviceMount(true);
      });
    });
  });

  useUnmount(() => {
    // if (isStartedAudio) {
    //   localAudio.stop();
    // }
    // if (isStartedVideo) {
    //   localVideo.stop();
    // }
  });

  useEffect(() => {
    if (cameraList.length > 0) {
      setActiveCamera(cameraList[0].deviceId);
    }
  }, [cameraList]);
  const history = useHistory();
  const hoge = () => {
    if (!videoRef.current) {
      return;
    }
    if (!previewCanvasRef.current) {
      requestAnimationFrame(hoge);
      return;
    }
    console.warn(1);
    const ctx = previewCanvasRef.current?.getContext('2d');
    ctx?.drawImage(videoRef.current, 0, 0, previewCanvasRef.current?.width, previewCanvasRef.current?.height);
    requestAnimationFrame(hoge);
  };
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // useEffect(() => {
  //   if (!videoRef.current) {
  //     return;
  //   }
  //   videoRef.current.addEventListener('play', hoge);
  //   return () => {
  //     videoRef.current?.removeEventListener('play', hoge);
  //   };
  // }, [videoRef, previewCanvasRef]);
  const onDisplayMediaSetting = async (value: boolean) => {
    setIsMediaSetting(value);

    if (isStartedVideo) {
      if (value) {
        await localVideo.stop();
      } else {
        if (deviceSettingCtx.virtualBackground !== 'none') {
          startLocalCanvasVideo(deviceSettingCtx.virtualBackground);
        } else {
          startLocalVideo();
        }
      }
    }
  };

  const onVirtualBackgroundChange = useCallback(
    async (item: SelectItem) => {
      const value = item.value;
      deviceSettingCtx.updateDeviceSetting((prev) => {
        return { ...prev, virtualBackground: value };
      });
      setLocalStorage('VirtualBackground', value);
      setActiceVirtualBackground(value);
    },
    [isStartedVideo, canvasRef]
  );

  const audioIcon = useMemo(() => {
    let iconType = '';
    if (isStartedAudio) {
      if (isMuted) {
        iconType = 'icon-audio-muted';
      } else {
        iconType = 'icon-audio-unmuted';
      }
    } else {
      if (isMicrophoneForbidden) {
        iconType = 'icon-audio-disallow';
      } else {
        iconType = 'icon-headset';
      }
    }
    if (iconType) {
      return <IconFont type={iconType} />;
    }
  }, [isMuted, isStartedAudio, isMicrophoneForbidden]);

  // useEffect(() => {
  //   if (isMediaSetting) {
  //     if (isStartedVideo) {
  //       localVideo.stop();
  //     }
  //   } else {
  //     if (!isStartedVideo) {
  //       if (deviceSettingCtx.virtualBackground === 'blur') {
  //         startLocalCanvasVideo();
  //       } else {
  //         startLocalVideo();
  //       }
  //     }
  //   }
  // }, [isMediaSetting]);

  const join = async () => {
    if (!userInfo) {
      errorCtx.setErrorState({
        error: new Error(),
        message: LANG.KT_9000_03
      });
      return;
    }
    if (!lessonInfo) {
      errorCtx.setErrorState({
        error: new Error(),
        message: LANG.KT_9000_03
      });
      return;
    }
    if (isStartedAudio) {
      await localAudio.stop();
    }
    if (isStartedVideo) {
      await localVideo.stop();
    }
    setIsJoining(true);

    // ログは非同期でOK、接続されることを優先
    sendLoggerService.sendLog({
      action: '入室ボタン押下',
      lessonId: lessonInfo?.lessonId ?? '',
      roomId: lessonInfo?.roomId ?? '',
      userId: userInfo?.userId ?? '',
      page: currentPage
    });
    lessonService
      .postLessonEnteringLog({
        userId: userInfo.userId,
        companyId: lessonInfo?.companyId,
        lessonId: lessonInfo?.lessonId,
        lessonLogType: {
          lesson_log_type: userInfo.role === 'TEACHER' ? LESSONLOGTYPE.Teacher : LESSONLOGTYPE.Learner
        }
      })
      .catch((err) => {
        console.error('loging failed.');
      });

    await joinService.joinRoom(userInfo, lessonInfo, zmClient);
    if (IS_DEMO) {
      history.push(`/video?lang=${systemLanguage}`);
    } else {
      history.push(`/video/${lessonInfo?.companyId}/${lessonInfo?.roomId}/?lang=${systemLanguage}`);
    }
  };
  const leaveLesson = () => {
    window.close();
  };

  // The canvas element will be deprecated. Please use the Videoplayer element to preview the virtual background.
  // video-playerを使うべきか

  if (isJoining) {
    return <LoadingLayer content={''} />;
  }

  return (
    <>
      <>
        <Settings
          isOpen={isMediaSetting}
          isInVBMode={isInVBMode}
          activeCamera={activeCamera}
          activeMicrophone={activeMicrophone}
          activeSpeaker={activeSpeaker}
          activeVirtualBackground={deviceSettingCtx.virtualBackground}
          cameraList={cameraList}
          micList={micList}
          speakerList={speakerList}
          onSwitchCamera={onSwitchCamera}
          onSwitchMicrophone={onSwitchMicrophone}
          onSwitchSpeaker={onSwitchSpeaker}
          onVirtualBackgroundChange={onVirtualBackgroundChange}
          onClose={() => {}}
          onDisplayMediaSetting={onDisplayMediaSetting}
        />
        {isMobile() ? (
          <Box>
            <Flex justifyContent={'center'} padding={'5px'}>
              <AppLogo
                style={{
                  width: '70%'
                }}
              />
            </Flex>
            <Heading size={'xl'} margin={'10px'}>
              {LANG.KT_0100_23}
            </Heading>
            <Flex flexDirection={'column'} justifyContent={'center'} alignItems={'center'} padding={'0 10px'}>
              <div className="container video-app">
                {/* <div className="preview-video"> */}
                <Box
                  maxWidth={'100vw'}
                  width={'350px'}
                  height={'200px'}
                  background={'rgba(0, 0, 0, 1)'}
                  borderRadius={'md'}
                  overflow={'hidden'}
                  className="preview-video"
                  position={'relative'}
                >
                  <video
                    className={classNames({ 'preview-video-show': !isInVBMode })}
                    muted={true}
                    playsInline
                    ref={videoRef}
                  />
                  <canvas
                    className={classNames({ 'preview-video-show': isInVBMode })}
                    width="1280"
                    height="720"
                    ref={canvasRef}
                  />
                  {/* </div> */}
                </Box>
              </div>
              <ChakraButton
                fontWeight={'bold'}
                fontSize={'sm'}
                border={'2px solid #aaaaaa'}
                width={'200px'}
                onClick={() => onCameraClick()}
                margin={'1em 0'}
                color={'#fff'}
                background={'#29a9ed'}
              >
                {isStartedVideo ? <VideoOnIcon /> : <VideoOffIcon />}
                {LANG.KT_0100_07}
              </ChakraButton>
              <ChakraButton
                fontWeight={'bold'}
                fontSize={'sm'}
                border={'2px solid #aaaaaa'}
                width={'200px'}
                onClick={() => onMicrophoneClick()}
                margin={'1em 0'}
                color={'#fff'}
                background={'#d26423'}
              >
                {audioIcon}
                {LANG.KT_0100_10}
              </ChakraButton>
              <Separator variant={'solid'} padding={'0 10px'} width={'100%'} />
              <SettingsButton
                // mediaSettingChildren={
                //   <Settings
                //     isOpen={isMediaSetting}
                //     isInVBMode={isInVBMode}
                //     activeCamera={activeCamera}
                //     activeMicrophone={activeMicrophone}
                //     activeSpeaker={activeSpeaker}
                //     activeVirtualBackground={activeVirtualBackground}
                //     cameraList={cameraList}
                //     micList={micList}
                //     speakerList={speakerList}
                //     onSwitchCamera={onSwitchCamera}
                //     onSwitchMicrophone={onSwitchMicrophone}
                //     onSwitchSpeaker={onSwitchSpeaker}
                //     onVirtualBackgroundChange={onVirtualBackgroundChange}
                //     onClose={() => {
                //       setIsMediaSetting(false);
                //     }}
                //     onDisplayMediaSetting={onDisplayMediaSetting}
                //   />
                // }
                onDisplayMediaSetting={onDisplayMediaSetting}
              />
              <Flex marginBottom={'auto'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'}>
                <ChakraButton
                  color={'#fff'}
                  margin={'10px'}
                  width={'200px'}
                  fontWeight={'bold'}
                  colorPalette={'blue'}
                  disabled={isInValidLesson || !completedDeviceMount}
                  onClick={() => {
                    join();
                  }}
                >
                  {LANG.KT_0100_05}
                </ChakraButton>
                <ChakraButton
                  margin={'10px'}
                  fontWeight={'bold'}
                  colorPalette={'gray'}
                  variant={'outline'}
                  onClick={leaveLesson}
                >
                  {LANG.KT_0100_29}
                </ChakraButton>
              </Flex>
            </Flex>
          </Box>
        ) : (
          <Box height={'100vh'}>
            <Flex alignItems={'center'} height={'100%'} background={'rgb(40, 42, 43)'} justifyContent={'center'}>
              <Box flex={1} position={'relative'}>
                <Flex
                  margin={'auto'}
                  width={'888px'}
                  height={'380px'}
                  overflow={'hidden'}
                  position={'relative'}
                  boxShadow={'0px 2px 4px 0px rgba(40, 42, 43, 0.3)'}
                  borderRadius={'8px'}
                  background={'#fff'}
                >
                  <Flex
                    width={'33%'}
                    justifyContent={'center'}
                    position={'relative'}
                    alignItems={'center'}
                    backgroundSize={'cover'}
                  >
                    <AppLogo
                      style={{
                        position: 'absolute',
                        width: '70%',
                        textAlign: 'center'
                      }}
                    />
                  </Flex>
                  <Flex
                    padding={'1em 2em'}
                    width={'100%'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    flexDirection={'column'}
                  >
                    <Heading size={'xl'} margin={'10px'}>
                      {LANG.KT_0100_23}
                    </Heading>
                    <Grid
                      justifyContent="center"
                      gridTemplateColumns={'1.5fr 1fr'}
                      gridTemplateRows={'1fr 10px 50px'}
                      placeItems={'center'}
                      placeContent={'center'}
                    >
                      <GridItem colSpan={1} width={'100%'}>
                        <Box
                          position={'relative'}
                          maxWidth={'100%'}
                          width={'350px'}
                          height={'200px'}
                          background={'#000'}
                          borderRadius={'md'}
                          overflow={'hidden'}
                        >
                          <div className="container video-app">
                            {/* <div className="preview-video"> */}
                            <Box
                              position={'relative'}
                              width={'350px'}
                              height={'200px'}
                              background={'rgba(0, 0, 0, 1)'}
                              borderRadius={'md'}
                              overflow={'hidden'}
                              className="preview-video"
                            >
                              <video
                                className={classNames({ 'preview-video-show': !isInVBMode })}
                                muted={true}
                                playsInline
                                ref={videoRef}
                              />
                              <canvas
                                className={classNames({ 'preview-video-show': isInVBMode })}
                                width="1280"
                                height="720"
                                ref={canvasRef}
                              />

                              {/* </div> */}
                            </Box>
                          </div>
                        </Box>
                      </GridItem>
                      <GridItem colSpan={1} margin={'10px'} placeItems={'center'}>
                        {/* <Box margin={'1em 0'}>
                        <CameraButton
                          isStartedVideo={isStartedVideo}
                          onCameraClick={onCameraClick}
                          onSwitchCamera={onSwitchCamera}
                          onBlurBackground={onBlurBackground}
                          cameraList={cameraList}
                          activeCamera={activeCamera}
                          isBlur={isBlur}
                          isPreview={true}
                        />
                      </Box>
                      <Box margin={'1em 0'}>
                        <MicrophoneButton
                          isStartedAudio={isStartedAudio}
                          isMuted={isMuted}
                          onMicrophoneClick={onMicrophoneClick}
                          onMicrophoneMenuClick={onMicrophoneMenuClick}
                          microphoneList={micList}
                          speakerList={speakerList}
                          activeMicrophone={activeMicrophone}
                          activeSpeaker={activeSpeaker}
                        />
                      </Box> */}

                        <ChakraButton
                          fontWeight={'bold'}
                          fontSize={'sm'}
                          border={'2px solid #aaaaaa'}
                          width={'200px'}
                          onClick={() => onCameraClick()}
                          margin={'1em 0'}
                          color={'#fff'}
                          background={'#29a9ed'}
                        >
                          {isStartedVideo ? <VideoCameraOutlined /> : <VideoCameraAddOutlined />}
                          {LANG.KT_0100_07}
                        </ChakraButton>
                        <ChakraButton
                          fontWeight={'bold'}
                          fontSize={'sm'}
                          border={'2px solid #aaaaaa'}
                          width={'200px'}
                          onClick={() => onMicrophoneClick()}
                          margin={'1em 0'}
                          color={'#fff'}
                          background={'#d26423'}
                        >
                          {audioIcon}
                          {LANG.KT_0100_10}
                        </ChakraButton>
                      </GridItem>
                      <GridItem colSpan={2} width={'100%'} flexBasis={'100%'} margin={'15px 0'}>
                        <Separator variant={'solid'} />
                      </GridItem>
                      <GridItem colSpan={1}>
                        <SettingsButton
                          // mediaSettingChildren={
                          //   <Settings
                          //     isOpen={isMediaSetting}
                          //     isInVBMode={isInVBMode}
                          //     activeCamera={activeCamera}
                          //     activeMicrophone={activeMicrophone}
                          //     activeSpeaker={activeSpeaker}
                          //     activeVirtualBackground={deviceSettingCtx.virtualBackground}
                          //     cameraList={cameraList}
                          //     micList={micList}
                          //     speakerList={speakerList}
                          //     onSwitchCamera={onSwitchCamera}
                          //     onSwitchMicrophone={onSwitchMicrophone}
                          //     onSwitchSpeaker={onSwitchSpeaker}
                          //     onVirtualBackgroundChange={onVirtualBackgroundChange}
                          //     onClose={() => {
                          //       setIsMediaSetting(false);
                          //     }}
                          //   />
                          // }
                          onDisplayMediaSetting={onDisplayMediaSetting}
                        />
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Flex>
                          <ChakraButton
                            fontWeight={'bold'}
                            colorPalette={'gray'}
                            marginRight={'10px'}
                            variant={'outline'}
                            onClick={leaveLesson}
                          >
                            {LANG.KT_0100_29}
                          </ChakraButton>
                          <ChakraButton
                            color={'#fff'}
                            fontWeight={'bold'}
                            colorPalette={'blue'}
                            marginLeft={'10px'}
                            disabled={isInValidLesson || !completedDeviceMount}
                            onClick={() => {
                              join();
                            }}
                          >
                            {LANG.KT_0100_05}
                          </ChakraButton>
                        </Flex>
                      </GridItem>
                    </Grid>
                  </Flex>
                </Flex>
              </Box>
            </Flex>
          </Box>
        )}
      </>
    </>
  );
};

export default PreviewContainer;
